/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument */
import { useCallback } from 'react';
import { useOrganizationsApi } from 'services/api';
import useSWR, { mutate } from 'swr';
import { LeaderRequest } from 'services/api/apis/organizations/organizations.api';
import { GetProfileResponse } from 'services/api/apis/profile/dto';

// Return type for useMyLeaderRequest hook
export interface UseMyLeaderRequestResult {
  data: LeaderRequest | null | undefined;
  error: Error | undefined;
  isValidating: boolean;
  revalidate: () => void;
}

// Custom hook to fetch user's leader request for an organization
export const useMyLeaderRequest = (open: boolean, selectedOrganizationId: string): UseMyLeaderRequestResult => {
  const organizationsApi = useOrganizationsApi();
  const swrKey = open && selectedOrganizationId
    ? [`/organizations/${selectedOrganizationId}/my-leader-request`]
    : null;

  const rLeaderRequest = useSWR<LeaderRequest | null>(
    swrKey,
    async () => {
      const result = await organizationsApi.getMyLeaderRequestForOrg(selectedOrganizationId);
      if (result.success) return result.value;
      return null;
    },
  );

  const revalidate = useCallback(() => {
    if (swrKey)
      void mutate(swrKey);
  }, [swrKey]);

  return {
    data: rLeaderRequest.data,
    error: rLeaderRequest.error as Error | undefined,
    isValidating: rLeaderRequest.isValidating,
    revalidate,
  };
};

// Custom hook for leader request eligibility
interface UseLeaderRequestEligibilityParams {
  profile: GetProfileResponse | undefined;
  selectedOrganizationId: string;
  orgLeaders: Array<{ id: string }>;
  myLeaderRequest: LeaderRequest | null | undefined;
}

export const useLeaderRequestEligibility = ({
  profile,
  selectedOrganizationId,
  orgLeaders,
  myLeaderRequest,
}: UseLeaderRequestEligibilityParams) => {
  const isAdmin = profile?.role?.includes('admin') || profile?.role?.includes('executive-admin');
  const isApprover = profile?.role?.includes('approver') || profile?.role?.includes('master');
  const isSchoolMember = !!profile?.school?.id;

  const isLeaderOfOrg = profile?.ledOrganizations?.some(
    org => org.id === selectedOrganizationId,
  ) || orgLeaders.some(leader => leader.id === profile?.id);

  // Only 'requested' status blocks re-application
  // 'declined' users can re-apply, 'accepted' users are already leaders
  const hasPendingRequest = myLeaderRequest?.status === 'requested';
  const hasAcceptedRequest = myLeaderRequest?.status === 'accepted';

  const isEligibleForLeaderRequest = Boolean(
    profile &&
    isSchoolMember &&
    !isAdmin &&
    !isApprover &&
    !isLeaderOfOrg &&
    !hasPendingRequest &&
    !hasAcceptedRequest,
  );

  return { isEligibleForLeaderRequest, hasPendingRequest };
};

// Custom hook for leader request actions
interface UseLeaderRequestActionsParams {
  selectedOrganizationId: string;
  myLeaderRequest: LeaderRequest | null | undefined;
  revalidateLeaderRequest: () => void;
  revalidateOrganization?: () => void;
}

export const useLeaderRequestActions = ({
  selectedOrganizationId,
  myLeaderRequest,
  revalidateLeaderRequest,
  revalidateOrganization,
}: UseLeaderRequestActionsParams) => {
  const organizationsApi = useOrganizationsApi();

  const handleRequestLeaderAccess = useCallback(async (role: string) => {
    const result = await organizationsApi.requestLeaderAccess(selectedOrganizationId, role);
    if (result.success)
      revalidateLeaderRequest();
    else
      throw new Error(result.error.message);
  }, [organizationsApi, selectedOrganizationId, revalidateLeaderRequest]);

  const handleUpdateLeaderRequest = useCallback(async (role: string) => {
    if (!myLeaderRequest?.id) return;
    const result = await organizationsApi.updateLeaderRequest(myLeaderRequest.id, role);
    if (result.success)
      revalidateLeaderRequest();
    else
      throw new Error(result.error.message);
  }, [organizationsApi, myLeaderRequest?.id, revalidateLeaderRequest]);

  const handleCancelLeaderRequest = useCallback(async () => {
    if (!myLeaderRequest?.id) return;
    const result = await organizationsApi.cancelLeaderRequest(myLeaderRequest.id);
    if (result.success)
      revalidateLeaderRequest();
    else
      throw new Error(result.error.message);
  }, [organizationsApi, myLeaderRequest?.id, revalidateLeaderRequest]);

  // Admin-invite handlers (for when admin invites user)
  const handleAcceptInvite = useCallback(async () => {
    if (!myLeaderRequest?.id) return;
    const result = await organizationsApi.acceptLeaderInvite(myLeaderRequest.id, true);
    if (result.success) {
      revalidateLeaderRequest();
      // Refresh organization data to update leaders list
      revalidateOrganization?.();
    } else {
      throw new Error(result.error.message);
    }
  }, [organizationsApi, myLeaderRequest?.id, revalidateLeaderRequest, revalidateOrganization]);

  const handleDeclineInvite = useCallback(async () => {
    if (!myLeaderRequest?.id) return;
    const result = await organizationsApi.declineLeaderInvite(myLeaderRequest.id);
    if (result.success)
      revalidateLeaderRequest();
    else
      throw new Error(result.error.message);
  }, [organizationsApi, myLeaderRequest?.id, revalidateLeaderRequest]);

  /**
   * CAM-684: Self-remove from the Organization Leaders list.
   * Reuses the existing removeLeader endpoint, passing the current user's id.
   */
  const handleLeaveMyRole = useCallback(async (userId: string) => {
    const result = await organizationsApi.removeLeaderFromOrganization(
      selectedOrganizationId,
      userId,
      { removeFromOrganization: false },
    );
    if (result.success) {
      revalidateLeaderRequest();
      revalidateOrganization?.();
    } else {
      throw new Error(result.error.message);
    }
  }, [organizationsApi, selectedOrganizationId, revalidateLeaderRequest, revalidateOrganization]);

  return {
    handleRequestLeaderAccess,
    handleUpdateLeaderRequest,
    handleCancelLeaderRequest,
    handleAcceptInvite,
    handleDeclineInvite,
    handleLeaveMyRole,
  };
};
